import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { testAPI, questionAPI, bookmarkAPI } from '../lib/api';
import { 
  ChevronLeft, ChevronRight, Flag, CheckCircle2, 
  MessageSquare, BookOpen, Trophy, Target, Bookmark as BookmarkIcon
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { renderLatex } from '../lib/renderLatex';
import LoadingSpinner from '../components/LoadingSpinner';

const AdvancedTest = () => {
  const { testId } = useParams();
  const navigate = useNavigate();
  useAuth();
  const [test, setTest] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [timeLeft, setTimeLeft] = useState(null);
  const [loading, setLoading] = useState(true);
  const [flagged, setFlagged] = useState(new Set());
  const [reviewed, setReviewed] = useState(new Set());
  const [bookmarked, setBookmarked] = useState(new Set());
  
  // Advanced Features
  const [isPaused] = useState(false);
  const [showNotes, setShowNotes] = useState(false);
  const [notes, setNotes] = useState({});

  // Performance tracking for advanced analytics
  const [questionTiming, setQuestionTiming] = useState({});
  const [questionStartTime, setQuestionStartTime] = useState(Date.now());
  const [visitCount, setVisitCount] = useState({}); // Tracks how many times each question was visited
  const [originalAnswers, setOriginalAnswers] = useState({}); // First answer before any changes
  const [skippedInitially, setSkippedInitially] = useState(new Set()); // Questions that were skipped then returned
  const [testStartTime] = useState(Date.now()); // When the test started

  const playSound = useCallback(() => {
    // Sound notification placeholder
  }, []);

  const handleAutoSubmit = useCallback(async () => {
    toast.info('Time is up! Auto-submitting...');
    try {
      const totalTimeSeconds = Math.floor((Date.now() - testStartTime) / 1000);
      
      // Build enhanced answer data for analytics (simplified version for auto-submit)
      const formattedAnswers = Object.entries(answers).map(([question_id, selected_answer]) => ({
        question_id,
        selected_answer,
        time_spent_seconds: questionTiming[question_id] || 0,
        times_visited: visitCount[question_id] || 1,
        answer_changed: originalAnswers[question_id] && originalAnswers[question_id] !== selected_answer,
        original_answer: originalAnswers[question_id] !== selected_answer ? originalAnswers[question_id] : null,
        was_marked_for_review: false,
        was_skipped_initially: skippedInitially.has(question_id),
      }));
      
      await testAPI.submit(testId, formattedAnswers, totalTimeSeconds);
      navigate(`/test/${testId}/results`);
    } catch (error) {
      toast.error('Failed to submit test');
    }
  }, [testId, answers, navigate, testStartTime, questionTiming, visitCount, originalAnswers, skippedInitially]);

  const loadTest = useCallback(async () => {
    try {
      const testRes = await testAPI.getById(testId);
      setTest(testRes.data);

      // Load questions with error handling - skip missing questions
      const loadedQuestions = [];
      for (const qId of testRes.data.questions) {
        try {
          const qRes = await questionAPI.getById(qId);
          loadedQuestions.push(qRes.data);
        } catch (err) {
          // Question not found, skipping
        }
      }
      
      if (loadedQuestions.length === 0) {
        toast.error('No valid questions found for this test');
        navigate('/exams');
        return;
      }
      
      setQuestions(loadedQuestions);

      // Calculate time based on exam config
      let timeInMinutes = 60; // default
      if (testRes.data.exam_config?.duration_minutes) {
        timeInMinutes = testRes.data.exam_config.duration_minutes;
      } else {
        timeInMinutes = testRes.data.questions.length; // 1 min per question
      }
      
      setTimeLeft(timeInMinutes * 60);

      setQuestionStartTime(Date.now());
    } catch (error) {
      toast.error('Failed to load test');
      navigate('/exams');
    } finally {
      setLoading(false);
    }
  }, [testId, navigate]);

  useEffect(() => {
    loadTest();
  }, [loadTest]);

  // Timer logic
  useEffect(() => {
    if (isPaused || timeLeft === null) return;

    const timer = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          playSound();
          handleAutoSubmit();
          return 0;
        }
        // Alert when 5 minutes left
        if (prev === 300) {
          playSound();
          toast.warning('⏰ 5 minutes remaining!');
        }
        // Alert when 1 minute left
        if (prev === 60) {
          playSound();
          toast.warning('⚠️ 1 minute left!');
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [isPaused, timeLeft, playSound, handleAutoSubmit]);

  const handleAnswerSelect = (answer) => {
    const questionId = questions[currentIndex].id;
    
    // Track time spent on question
    const timeSpent = Math.floor((Date.now() - questionStartTime) / 1000);
    setQuestionTiming({
      ...questionTiming,
      [questionId]: (questionTiming[questionId] || 0) + timeSpent,
    });

    // Track original answer (first answer before any changes)
    if (!originalAnswers[questionId] && !answers[questionId]) {
      setOriginalAnswers({
        ...originalAnswers,
        [questionId]: answer,
      });
    }

    // Update answer
    setAnswers({
      ...answers,
      [questionId]: answer,
    });

    // Play success sound
    playSound();

    // Auto-mark as reviewed when answered
    const newReviewed = new Set(reviewed);
    newReviewed.add(currentIndex);
    setReviewed(newReviewed);
  };

  const handleSubmit = async () => {
    // Calculate total time from test start
    const totalTimeSeconds = Math.floor((Date.now() - testStartTime) / 1000);
    
    // Build enhanced answer data for advanced analytics
    const formattedAnswers = questions.map((q, idx) => {
      const questionId = q.id;
      const selectedAnswer = answers[questionId] || null;
      const originalAnswer = originalAnswers[questionId];
      const wasAnswerChanged = originalAnswer && originalAnswer !== selectedAnswer;
      
      return {
        question_id: questionId,
        selected_answer: selectedAnswer,
        time_spent_seconds: questionTiming[questionId] || 0,
        times_visited: visitCount[questionId] || 1,
        answer_changed: wasAnswerChanged,
        original_answer: wasAnswerChanged ? originalAnswer : null,
        was_marked_for_review: flagged.has(idx),
        was_skipped_initially: skippedInitially.has(questionId),
        question_position: idx + 1,
      };
    });

    try {
      await testAPI.submit(testId, formattedAnswers, totalTimeSeconds);
      toast.success('🎉 Test submitted successfully!');
      navigate(`/test/${testId}/results`);
    } catch (error) {
      toast.error('Failed to submit test');
    }
  };

  const formatTime = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    
    if (hours > 0) {
      return `${hours}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const toggleFlag = () => {
    const newFlagged = new Set(flagged);
    if (newFlagged.has(currentIndex)) {
      newFlagged.delete(currentIndex);
    } else {
      newFlagged.add(currentIndex);
    }
    setFlagged(newFlagged);
  };

  const toggleBookmark = async () => {
    const questionId = questions[currentIndex]?.id;
    if (!questionId) return;
    
    try {
      const newBookmarked = new Set(bookmarked);
      if (newBookmarked.has(questionId)) {
        await bookmarkAPI.remove(questionId);
        newBookmarked.delete(questionId);
        toast.success('Bookmark removed');
      } else {
        await bookmarkAPI.add(questionId);
        newBookmarked.add(questionId);
        toast.success('Question bookmarked');
      }
      setBookmarked(newBookmarked);
    } catch (error) {
      toast.error('Failed to update bookmark');
    }
  };

  const getProgressStats = () => {
    const total = questions.length;
    const answered = Object.keys(answers).length;
    const flaggedCount = flagged.size;
    const unattempted = total - answered;
    
    return { total, answered, flaggedCount, unattempted };
  };

  const navigateToQuestion = (index) => {
    // Track time on current question
    const timeSpent = Math.floor((Date.now() - questionStartTime) / 1000);
    if (currentIndex < questions.length) {
      const currentQ = questions[currentIndex];
      if (currentQ) {
        setQuestionTiming({
          ...questionTiming,
          [currentQ.id]: (questionTiming[currentQ.id] || 0) + timeSpent,
        });
        
        // Track if question was skipped (navigating away without answering)
        if (!answers[currentQ.id]) {
          setSkippedInitially(prev => new Set([...prev, currentQ.id]));
        }
      }
    }
    
    // Track visit count for the question we're going to
    const targetQ = questions[index];
    if (targetQ) {
      setVisitCount(prev => ({
        ...prev,
        [targetQ.id]: (prev[targetQ.id] || 0) + 1
      }));
    }
    
    setCurrentIndex(index);
    setQuestionStartTime(Date.now());
  };

  const navigateQuestion = (direction) => {
    const newIndex = currentIndex + direction;
    if (newIndex >= 0 && newIndex < questions.length) {
      navigateToQuestion(newIndex);
    }
  };

  const handleNoteSave = (text) => {
    setNotes({
      ...notes,
      [questions[currentIndex]?.id]: text,
    });
  };

  if (loading) {
    return <LoadingSpinner size="xl" text="Loading your test..." fullScreen={true} />;
  }

  if (!test || questions.length === 0) {
    return <div className="p-8 text-center text-destructive bg-background min-h-screen">Failed to load test</div>;
  }

  const currentQuestion = questions[currentIndex];
  const stats = getProgressStats();

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Cozy Student Style Header */}
      <div className="sticky top-0 z-40 bg-white/95 dark:bg-gray-900/95 backdrop-blur border-b border-gray-200 dark:border-gray-800 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            {/* Left: ExamDex + Exam Title */}
            <div className="flex items-center gap-4">
              {/* ExamDex Branding */}
              <div className="flex items-center gap-2">
                <div className="w-14 h-14 rounded-xl flex items-center justify-center shadow-md overflow-hidden">
                  <img src="/logo192.png" alt="ExamDex" className="w-full h-full object-cover" />
                </div>
                <span className="text-sm font-bold text-blue-600 dark:text-blue-400 hidden md:inline">ExamDex</span>
              </div>
              <div className="h-8 w-px bg-gray-200 dark:bg-gray-700 hidden sm:block"></div>
              <div>
                <h2 className="text-lg font-bold text-foreground">
                  {test?.exam_name || 'Test'}
                </h2>
                <p className="text-xs text-primary font-medium">
                  Question {currentIndex + 1} of {questions.length}
                </p>
              </div>
            </div>

            {/* Center: Progress - Cozy Style */}
            <div className="hidden md:flex items-center gap-4">
              <div className="px-4 py-2 bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-700 rounded-xl text-center">
                <p className="text-xs text-emerald-600 dark:text-emerald-400 font-medium">✓ Answered</p>
                <p className="text-xl font-bold text-emerald-600 dark:text-emerald-400">{stats.answered}</p>
              </div>
              <div className="px-4 py-2 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-xl text-center">
                <p className="text-xs text-amber-600 dark:text-amber-400 font-medium">🚩 Flagged</p>
                <p className="text-xl font-bold text-amber-600 dark:text-amber-400">{stats.flaggedCount}</p>
              </div>
              <div className="px-4 py-2 bg-gray-100 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl text-center">
                <p className="text-xs text-muted-foreground font-medium">⏳ Remaining</p>
                <p className="text-xl font-bold text-muted-foreground">{stats.unattempted}</p>
              </div>
            </div>

            {/* Right: Time - Cozy Style */}
            <div className="flex items-center gap-4">
              <div className={`text-center px-4 py-2 rounded-xl border ${
                timeLeft > 300 
                  ? 'bg-emerald-50 dark:bg-emerald-900/20 border-emerald-200 dark:border-emerald-700' 
                  : timeLeft > 60 
                  ? 'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-700' 
                  : 'bg-rose-50 dark:bg-rose-900/20 border-rose-200 dark:border-rose-700 animate-pulse'
              }`}>
                <p className={`text-xs font-medium ${
                  timeLeft > 300 ? 'text-emerald-600 dark:text-emerald-400' :
                  timeLeft > 60 ? 'text-amber-600 dark:text-amber-400' :
                  'text-rose-600 dark:text-rose-400'
                }`}>
                  ⏱️ Time Left
                </p>
                <p className={`text-2xl font-mono font-bold ${
                  timeLeft > 300 ? 'text-emerald-600 dark:text-emerald-400' :
                  timeLeft > 60 ? 'text-amber-600 dark:text-amber-400' :
                  'text-rose-600 dark:text-rose-400'
                }`}>
                  {formatTime(timeLeft)}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 py-6">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Question Panel */}
          <div className="lg:col-span-3 space-y-4">
            {/* Question Card - Cozy Style */}
            <Card className="bg-white dark:bg-gray-800 shadow-sm rounded-2xl p-8 min-h-[500px] flex flex-col border border-gray-200 dark:border-gray-700">
              {/* Meta */}
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3 flex-wrap">
                  {/* Question Type Badge */}
                  <span className={`px-3 py-1 rounded-full text-xs font-medium border ${
                    currentQuestion.question_type === 'Numerical' || (!currentQuestion.options || currentQuestion.options.length === 0)
                      ? 'bg-violet-50 dark:bg-violet-900/20 text-violet-600 dark:text-violet-400 border-violet-200 dark:border-violet-700'
                      : 'bg-sky-50 dark:bg-sky-900/20 text-sky-600 dark:text-sky-400 border-sky-200 dark:border-sky-700'
                  }`}>
                    {currentQuestion.question_type === 'Numerical' || (!currentQuestion.options || currentQuestion.options.length === 0) 
                      ? '🔢 Numerical' 
                      : '📝 MCQ'}
                  </span>
                  {/* Subject Badge */}
                  {currentQuestion.subject_name && (
                    <span className="px-3 py-1 rounded-full text-xs font-medium border bg-primary/10 text-primary border-primary/30">
                      📚 {currentQuestion.subject_name}
                    </span>
                  )}
                </div>
                <div className="flex gap-2">
                  <Button
                    onClick={toggleBookmark}
                    variant="ghost"
                    className={`rounded-xl border ${bookmarked.has(questions[currentIndex]?.id) ? 'text-blue-600 bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-700' : 'text-muted-foreground border-gray-200 dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-800'}`}
                    title="Bookmark question"
                  >
                    <BookmarkIcon size={20} fill={bookmarked.has(questions[currentIndex]?.id) ? 'currentColor' : 'none'} />
                  </Button>
                  <Button
                    onClick={toggleFlag}
                    variant="ghost"
                    className={`rounded-xl border ${flagged.has(currentIndex) ? 'text-amber-600 bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-700' : 'text-muted-foreground border-gray-200 dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-800'}`}
                  >
                    <Flag size={20} fill={flagged.has(currentIndex) ? 'currentColor' : 'none'} />
                  </Button>
                </div>
              </div>

              {/* Question Text */}
              <div className="mb-8 flex-1">
                <h3 
                  className="text-xl font-semibold text-foreground mb-6"
                  dangerouslySetInnerHTML={{ __html: renderLatex(currentQuestion.question_text) }}
                />

                {/* Render based on question type */}
                {(currentQuestion.question_type === 'Numerical' || !currentQuestion.options || currentQuestion.options.length === 0) ? (
                  /* Numerical Input - Cozy Style */
                  <div className="space-y-4">
                    <label className="block text-sm font-medium text-violet-600 dark:text-violet-400 mb-2">
                      🔢 Enter your numerical answer:
                    </label>
                    <Input
                      type="number"
                      step="any"
                      value={answers[currentQuestion.id] || ''}
                      onChange={(e) => handleAnswerSelect(e.target.value)}
                      placeholder="Enter value..."
                      className="w-full max-w-md text-lg p-4 text-center font-mono text-foreground bg-violet-50 dark:bg-violet-900/20 border border-violet-200 dark:border-violet-700 rounded-xl focus:border-violet-500 focus:ring-2 focus:ring-violet-500/20"
                    />
                    <p className="text-xs text-muted-foreground">
                      💡 Tip: Enter decimal values up to 2 decimal places
                    </p>
                  </div>
                ) : (
                  /* MCQ Options - Cozy Style */
                  <div className="space-y-3">
                    {currentQuestion.options.map((option, idx) => (
                      <button
                        key={idx}
                        onClick={() => handleAnswerSelect(option.label)}
                        className={`w-full p-4 rounded-xl text-left border transition-all ${
                          answers[currentQuestion.id] === option.label
                            ? 'border-primary bg-primary/10 shadow-sm'
                            : 'border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 hover:border-primary/50 hover:bg-primary/5'
                        }`}
                      >
                        <div className="flex items-start gap-4">
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm flex-shrink-0 ${
                            answers[currentQuestion.id] === option.label
                              ? 'bg-primary text-white'
                              : 'bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-300'
                          }`}>
                            {option.label}
                          </div>
                          <span 
                            className="pt-1 text-foreground"
                            dangerouslySetInnerHTML={{ __html: renderLatex(option.text) }}
                          />
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Notes Section - Cozy Style */}
              {showNotes && (
                <div className="border-t border-gray-200 dark:border-gray-700 pt-4 mt-4">
                  <textarea
                    value={notes[currentQuestion.id] || ''}
                    onChange={(e) => handleNoteSave(e.target.value)}
                    placeholder="Add notes here..."
                    className="w-full h-20 bg-gray-50 dark:bg-gray-800 text-foreground p-3 rounded-xl border border-gray-200 dark:border-gray-700 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 resize-none"
                  />
                </div>
              )}
            </Card>

            {/* Footer Actions - Cozy Style */}
            <div className="flex gap-3 flex-wrap">
              <Button
                onClick={() => navigateQuestion(-1)}
                disabled={currentIndex === 0}
                className="flex items-center gap-2 px-6 py-3 bg-white dark:bg-gray-800 text-foreground hover:bg-gray-100 dark:hover:bg-gray-700 border border-gray-200 dark:border-gray-700 rounded-xl disabled:opacity-50"
              >
                <ChevronLeft size={18} />
                Previous
              </Button>

              <Button
                onClick={() => setShowNotes(!showNotes)}
                className="flex items-center gap-2 px-6 py-3 bg-white dark:bg-gray-800 text-foreground hover:bg-gray-100 dark:hover:bg-gray-700 border border-gray-200 dark:border-gray-700 rounded-xl"
              >
                <MessageSquare size={18} />
                Notes
              </Button>

              <Button
                onClick={toggleFlag}
                className={`flex items-center gap-2 px-6 py-3 rounded-xl border ${
                  flagged.has(currentIndex)
                    ? 'bg-amber-50 dark:bg-amber-900/20 text-amber-600 dark:text-amber-400 hover:bg-amber-100 dark:hover:bg-amber-900/30 border-amber-200 dark:border-amber-700'
                    : 'bg-white dark:bg-gray-800 text-foreground hover:bg-gray-100 dark:hover:bg-gray-700 border-gray-200 dark:border-gray-700'
                }`}
              >
                <Flag size={18} />
                {flagged.has(currentIndex) ? 'Flagged' : 'Flag'}
              </Button>

              <Button
                onClick={() => navigateQuestion(1)}
                disabled={currentIndex === questions.length - 1}
                className="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-primary text-white hover:bg-primary/90 rounded-xl disabled:opacity-50 font-medium shadow-sm transition-all"
              >
                Next
                <ChevronRight size={18} />
              </Button>
            </div>
          </div>

          {/* Right Sidebar - Cozy Style */}
          <div className="space-y-4">
            {/* Quick Stats */}
            <Card className="bg-white dark:bg-gray-900/50 border border-emerald-200 dark:border-emerald-700/50 shadow-sm p-4 rounded-2xl">
              <h3 className="font-medium text-foreground mb-3 flex items-center gap-2">
                <Trophy className="text-amber-500" size={18} />
                Your Progress
              </h3>
              <div className="space-y-3">
                <div>
                  <div className="flex justify-between text-xs mb-1">
                    <span className="text-muted-foreground">Answered</span>
                    <span className="font-medium text-emerald-600 dark:text-emerald-400">{stats.answered}/{stats.total}</span>
                  </div>
                  <div className="h-3 bg-gray-100 dark:bg-gray-800 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-gradient-to-r from-emerald-500 to-teal-400 rounded-full"
                      style={{ width: `${(stats.answered / stats.total) * 100}%` }}
                    />
                  </div>
                </div>
              </div>
            </Card>

            {/* Question Navigator - Cozy Style */}
            <Card className="bg-white dark:bg-gray-900/50 border border-gray-200 dark:border-gray-700 shadow-sm p-4 rounded-2xl max-h-96 overflow-y-auto">
              <h3 className="font-medium text-foreground mb-3 flex items-center gap-2">
                <Target className="text-primary" size={18} />
                Questions
              </h3>
              <div className="grid grid-cols-5 gap-2">
                {questions.map((q, idx) => (
                  <button
                    key={idx}
                    onClick={() => navigateToQuestion(idx)}
                    className={`aspect-square rounded-lg text-xs font-medium transition-all border ${
                      idx === currentIndex
                        ? 'bg-primary/10 text-primary border-primary shadow-sm'
                        : answers[q.id]
                        ? 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-600 dark:text-emerald-400 border-emerald-200 dark:border-emerald-700'
                        : flagged.has(idx)
                        ? 'bg-amber-50 dark:bg-amber-900/20 text-amber-600 dark:text-amber-400 border-amber-200 dark:border-amber-700'
                        : 'bg-gray-50 dark:bg-gray-800 text-muted-foreground border-gray-200 dark:border-gray-700 hover:border-primary/50'
                    }`}
                  >
                    {idx + 1}
                  </button>
                ))}
              </div>
            </Card>

            {/* Submit Button - Cozy Style */}
            <Button
              onClick={handleSubmit}
              className="w-full h-14 rounded-xl bg-emerald-500 hover:bg-emerald-600 text-white font-medium text-lg shadow-sm hover:shadow-md transition-all"
            >
              <CheckCircle2 size={20} className="mr-2" />
              Submit Test
            </Button>

            {/* Legend - Cozy Style */}
            <Card className="bg-white dark:bg-gray-900/50 border border-gray-200 dark:border-gray-700 p-4 rounded-2xl text-xs">
              <h3 className="font-medium text-foreground mb-3">Legend</h3>
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <div className="w-6 h-6 rounded-lg bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-700"></div>
                  <span className="text-emerald-600 dark:text-emerald-400">✓ Answered</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-6 h-6 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700"></div>
                  <span className="text-amber-600 dark:text-amber-400">🚩 Flagged</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-6 h-6 rounded-lg bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700"></div>
                  <span className="text-muted-foreground">⏳ Pending</span>
                </div>
              </div>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdvancedTest;
